import { afterNextRender, AfterRenderRef, DestroyRef, Directive, ElementRef, inject, signal, WritableSignal } from '@angular/core';

const supportedDisplays: string[] = ['flex', 'inline-flex'];

/**
 * Draws a shadow on each horizontally overflowing edge of a flex container.
 */
@Directive({
  selector: '[uiScrollIndicator]',
  host: {
    '[class.ui-scroll-indicator]': 'scrollable()',
    '[class.ui-scroll-indicator-scrolled]': 'scrolled()',
    '[class.ui-scroll-indicator-scroll-end]': 'scrollEnd()',
    '[style.--ui-scroll-indicator-gap.px]': 'gap()',
    '(scroll)': 'update()',
  },
})
export class ScrollIndicatorDirective {
  protected readonly scrollable: WritableSignal<boolean> = signal(false);
  protected readonly scrolled: WritableSignal<boolean> = signal(false);
  protected readonly scrollEnd: WritableSignal<boolean> = signal(false);
  protected readonly gap: WritableSignal<number> = signal(0);

  private readonly elementRef: ElementRef<HTMLElement> = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly supportedLayout: WritableSignal<boolean> = signal(false);

  private readonly observeAfterRenderRef: AfterRenderRef = afterNextRender(() => void this.observe());

  public update(): void {
    const element: HTMLElement = this.elementRef.nativeElement;
    const maxScroll: number = element.scrollWidth - element.clientWidth;
    const position: number = element.scrollLeft < 0 ? maxScroll + element.scrollLeft : element.scrollLeft;
    const scrollable: boolean = this.supportedLayout() && maxScroll > 1;

    this.scrollable.set(scrollable);
    this.scrolled.set(scrollable && position > 1);
    this.scrollEnd.set(scrollable && position >= maxScroll - 1);
  }

  public measure(): void {
    const style: CSSStyleDeclaration = getComputedStyle(this.elementRef.nativeElement);

    this.gap.set(parseFloat(style.columnGap) || 0);
    this.supportedLayout.set(supportedDisplays.includes(style.display) && !style.justifyContent.startsWith('space-'));
    this.update();
  }

  private observe(): void {
    const element: HTMLElement = this.elementRef.nativeElement;
    const resizeObserver: ResizeObserver = new ResizeObserver(() => void this.measure());
    const mutationObserver: MutationObserver = new MutationObserver(() => void this.update());

    resizeObserver.observe(element);
    mutationObserver.observe(element, { childList: true, subtree: true, characterData: true });
    this.destroyRef.onDestroy(() => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    });
    this.measure();
  }
}
